import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navbar } from '../../components/layout/navbar';
import { Footer } from '../../components/layout/footer';
import { ForumDiscussionCard } from '../../components/forum/forum-post-card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ForumBackground } from '../../components/forum/forum-background';
import { ArrowLeft, TrendingUp, Clock, Filter, PenSquare } from 'lucide-react';

// Direct imports
import { useForum } from '../../hooks/use-forum';
import { useAuth } from '../../hooks/use-auth';

export default function CategoryPage() {
  const { categorySlug } = useParams();
  const [activeTab, setActiveTab] = useState('latest');
  const [activeTimeFilter, setActiveTimeFilter] = useState('all-time');

  // Use hooks directly with the correct function names
  const { user } = useAuth();
  const {
    useGetPostsByCategory,
    useGetCategories,
    useGetCategoryBySlug,
    upvotePost,
    downvotePost,
  } = useForum();

  // Get category data
  const { data: category, isLoading: isLoadingCategory } =
    useGetCategoryBySlug(categorySlug);

  // Get posts for this category
  const { data: posts = [], isLoading: isLoadingPosts } =
    useGetPostsByCategory(categorySlug);

  // Sort posts based on active tab
  const sortedPosts = [...(posts || [])].sort((a, b) => {
    if (activeTab === 'trending') {
      // Sort by popularity (upvotes - downvotes)
      return b.upvotes - b.downvotes - (a.upvotes - a.downvotes);
    } else if (activeTab === 'latest') {
      // Sort by date (newest first)
      return new Date(b.createdAt) - new Date(a.createdAt);
    }
    // Default sort by date
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  return (
    <div className="flex min-h-screen flex-col bg-black text-white">
      {/* Gradient Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
      </div>

      <Navbar />
      <ForumBackground />
      <main className="flex-1 relative z-10">
        <div className="container mx-auto px-4 py-8 md:px-6">
          <Link
            to="/forum"
            className="inline-flex items-center text-sm text-zinc-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Forum
          </Link>

          {isLoadingCategory ? (
            <div className="animate-pulse">
              <div className="h-8 bg-zinc-800 rounded w-1/4 mb-2"></div>
              <div className="h-4 bg-zinc-800 rounded w-2/3 mb-8"></div>
            </div>
          ) : (
            <>
              <h1 className="text-3xl font-bold mb-2 text-white">
                {category?.name}
              </h1>
              <p className="text-zinc-400 mb-8">{category?.description}</p>
            </>
          )}

          <div className="flex items-center justify-between mb-6">
            <Tabs
              defaultValue={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="bg-transparent p-0 h-auto">
                <TabsTrigger
                  value="latest"
                  className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-purple-500 data-[state=active]:text-purple-400 data-[state=active]:shadow-none rounded-none px-4 py-2 text-zinc-400 hover:text-white"
                >
                  Latest
                </TabsTrigger>
                <TabsTrigger
                  value="trending"
                  className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-purple-500 data-[state=active]:text-purple-400 data-[state=active]:shadow-none rounded-none px-4 py-2 text-zinc-400 hover:text-white"
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Trending
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="hidden md:flex border-white/10 text-zinc-300 hover:text-white hover:bg-white/5"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filter
              </Button>
              <Link to="/forum/create">
                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <PenSquare className="h-4 w-4 mr-2" />
                  New Post
                </Button>
              </Link>
            </div>
          </div>

          {/* Time filter */}
          <div className="flex items-center gap-4 mb-8 text-sm border-b border-white/10 pb-2">
            <span className="text-zinc-400 flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              Time:
            </span>
            <button
              className={`px-2 py-1 rounded-md transition-colors ${
                activeTimeFilter === 'today'
                  ? 'bg-white/10 text-white font-medium'
                  : 'text-zinc-400 hover:text-white hover:bg-white/5'
              }`}
              onClick={() => setActiveTimeFilter('today')}
            >
              Today
            </button>
            <button
              className={`px-2 py-1 rounded-md transition-colors ${
                activeTimeFilter === 'week'
                  ? 'bg-white/10 text-white font-medium'
                  : 'text-zinc-400 hover:text-white hover:bg-white/5'
              }`}
              onClick={() => setActiveTimeFilter('week')}
            >
              This Week
            </button>
            <button
              className={`px-2 py-1 rounded-md transition-colors ${
                activeTimeFilter === 'month'
                  ? 'bg-white/10 text-white font-medium'
                  : 'text-zinc-400 hover:text-white hover:bg-white/5'
              }`}
              onClick={() => setActiveTimeFilter('month')}
            >
              This Month
            </button>
            <button
              className={`px-2 py-1 rounded-md transition-colors ${
                activeTimeFilter === 'all-time'
                  ? 'bg-white/10 text-white font-medium'
                  : 'text-zinc-400 hover:text-white hover:bg-white/5'
              }`}
              onClick={() => setActiveTimeFilter('all-time')}
            >
              All Time
            </button>
          </div>

          {isLoadingPosts ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-zinc-900/50 rounded-lg shadow-sm border border-white/10 p-4 animate-pulse"
                >
                  <div className="h-6 bg-zinc-800 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-zinc-800 rounded w-1/4 mb-4"></div>
                  <div className="h-16 bg-zinc-800 rounded w-full mb-4"></div>
                  <div className="flex justify-between">
                    <div className="h-4 bg-zinc-800 rounded w-1/4"></div>
                    <div className="h-4 bg-zinc-800 rounded w-1/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : sortedPosts.length > 0 ? (
            <div className="space-y-4">
              {sortedPosts.map((post) => (
                <ForumDiscussionCard
                  key={post.id}
                  discussion={post}
                  user={user}
                  onUpvote={() =>
                    upvotePost.mutate({
                      discussionId: post.oid,
                      uOID: user?.oid,
                    })
                  }
                  onDownvote={() =>
                    downvotePost.mutate({
                      discussionId: post.oid,
                      uOID: user?.oid,
                    })
                  }
                  category={category}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 border border-white/10 rounded-lg bg-zinc-900/30">
              <h3 className="text-xl font-bold mb-2 text-white">
                No posts found
              </h3>
              <p className="text-zinc-400 mb-4">
                There are no posts in this category yet. Be the first to create
                a post!
              </p>
              <Link to="/forum/create">
                <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                  Create Post
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
